import * as React from "react";
import {
  Grid,
  Typography,
  Box,
  Switch,
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  Link,
} from "@mui/material";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { refreshAllCustomerPermissions } from "../hooks/useCustomerPermission";

interface Props {
  organizationId: any;
  setPermitted: any;
}

const OrganizationPermissions: React.FC<Props> = ({
  organizationId,
  setPermitted,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  

  const [permissions, setPermissions] = React.useState<any>([]);
  const [organizationDefaultPrivileges, setOrganizationDefaultPrivileges] =
    React.useState<any>([]);
  const [error, setError] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showDefaultPrivilegeSection, setShowDefaultPrivilegeSection] =
    React.useState<boolean>(false);

  const permissionType = "Billing Access";

  const fetchSpecificCustomerPermission = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/customers/${organizationId}/get_specific_customer_permission?type=${permissionType}`
      );
      const data = await response.json();
      setPermitted(data.resource);
    } catch (error) {
      console.error("Error getting permission", error);
      setPermitted(false);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchSpecificCustomerPermission();
  }, [organizationDefaultPrivileges]);

  const getOrganizationPermissions = () => {
    setLoading(true);
    fetch(`/customer_permissions/${organizationId}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success) {
          setPermissions(result.resource.organization_permissions);
          setOrganizationDefaultPrivileges(
            result.resource.organization_default_privilege
          );
          setLoading(false);
        } else {
          console.log(result.error);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  React.useEffect(() => {
    getOrganizationPermissions();
  }, [organizationId]);

  const updatePrivilegeState = (event) => {
    const permissionId = event.target.value;
    const permitted = event.target.checked;
    fetch(`/customer_permissions/${permissionId}`, {
      method: "PUT",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        customer_permission_control: {
          permitted: permitted,
        },
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          setError(result.message);
        } else {
          getOrganizationPermissions();
          refreshAllCustomerPermissions();
        }
      })
      .catch((error) => {
        setError(error);
      });
  };

  const updateDefaultPrivilegeState = (event) => {
    const privilege_id = event.target.value;
    const checked = event.target.checked;

    fetch(`/customer_default_privileges/${privilege_id}`, {
      method: "PUT",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({
        customer_default_privilege: {
          checked,
        },
      }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          setError(result.message);
        } else {
          getOrganizationPermissions();
        }
      })
      .catch((error) => {
        setError(error);
      });
  };

  const handleDefaultPrivilegesSection = () =>
    setShowDefaultPrivilegeSection(!showDefaultPrivilegeSection);

  return (
    <Grid item xs={12} className="info-container">
      
      <div className="overview-container row">
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "18px",
            letterSpacing: "1px",
            color: "#4A4442",
          }}
        >
          Organization Permissions
        </Typography>
      </div>

      <div className="divider" style={{ margin: "20px 0px" }}></div>

      <Grid container>

        {permissions.map((permission) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginLeft: "-9px",
              width: "100%",
            }}
          >
            <Switch
              value={permission.id}
              checked={permission.permitted}
              onChange={updatePrivilegeState}
              color="primary"
            />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.15px",
                lineHeight: "20px",
              }}
            >
              {permission.permission_name}
            </Typography>
          </Box>
        ))}
      </Grid>

      <Typography
        style={{
          fontWeight: 700,
          fontSize: "12px",
          lineHeight: "22px",
          color: "black",
          marginTop: "16px",
        }}
      >
        <Link
          sx={{ color: "#2B6CB0", textDecoration: "underline" }}
          onClick={handleDefaultPrivilegesSection}
        >
          Update
        </Link>
        &nbsp;the default privileges when a new provider is added to this
        organization
      </Typography>

      {showDefaultPrivilegeSection && (
        <Grid item xs={12} className="content">
          <ul
            className="privilegesList"
            style={{ margin: "24px 0px 20px 0px" }}
          >
            {organizationDefaultPrivileges?.map((privilege) => (
              <li
                key={privilege.id}
                style={{
                  display: "flex",
                  columnGap: "10px",
                  alignItems: "center",
                  marginLeft: "-9px",
                  marginBottom: "4px",
                }}
              >
                <div className="privilegeToggle">
                  <Switch
                    value={privilege.id}
                    checked={privilege.state}
                    onChange={updateDefaultPrivilegeState}
                    color="primary"
                  />
                </div>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    letterSpacing: "0.15px",
                  }}
                >
                  {privilege.name}
                </Typography>
              </li>
            ))}
          </ul>
        </Grid>
      )}

      <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress />
      </Backdrop>
    </Grid>
  );
};

export default OrganizationPermissions;
